import { type LandingSectionDefinition, type LandingSectionField } from "../../fields";
import {
  bikiniBoxesProps,
  bikiniFaqProps,
  bikiniFeaturesProps,
  bikiniFooterProps,
  bikiniHeroProps,
  bikiniHowItWorksProps,
  bikiniIncludedProps,
  bikiniMetamorphosesProps,
  bikiniMoneyBackProps,
  bikiniNonForbiddenProductsProps,
  bikiniPriceBannerProps,
  bikiniRecommendationsProps,
  bikiniStatNumbersProps,
  bikiniStickyBarProps,
  bikiniVideoProps,
  bikiniWaysProps,
} from "./templates";
import { type bikiniSectionTypes } from "./types";

const GROUP = "6 Tygodni do Bikini";

const ICON_OPTIONS = [
  { label: "Zegar", value: "clock" },
  { label: "Monety w dłoni", value: "handCoins" },
  { label: "Sałatka", value: "salad" },
];

const ctaFields = (buttonText = "Tekst przycisku"): LandingSectionField[] => [
  { key: "buttonText", label: buttonText, type: "text" },
  {
    key: "href",
    label: "Link przycisku",
    type: "url",
    help: "Puste = następny krok funnelu (checkout).",
  },
];

const classNameField: LandingSectionField = {
  key: "className",
  label: "Klasy CSS sekcji",
  type: "text",
  help: "Klasy Tailwind dodane do sekcji, np. kolor tła.",
};

const TRANSFORMATION_SET_OPTIONS = [
  { label: "Top metamorfozy (10 największych spadków wagi)", value: "topHero" },
  { label: "Kolejne metamorfozy z filmem (miejsca 11-40)", value: "topStatistics" },
  { label: "Wszystkie metamorfozy z filmem", value: "allWithVideo" },
  { label: "Własna lista (identyfikatory poniżej)", value: "custom" },
];

const cardsFields = (index: 1 | 2): LandingSectionField[] => [
  {
    key: `cards${index}Set`,
    label: `Karty metamorfoz w karuzeli ${index}`,
    type: "select",
    options: TRANSFORMATION_SET_OPTIONS,
    help: "Używane w trybie kart Hollywood.",
  },
  {
    key: `cards${index}Ids`,
    label: `Identyfikatory metamorfoz w karuzeli ${index}`,
    type: "stringList",
    help: "Dla własnej listy: jeden identyfikator w linii (np. 30), w kolejności wyświetlania.",
  },
];

const paragraphsField = (key: string, label: string): LandingSectionField => ({
  key,
  label,
  type: "list",
  itemLabel: "Akapit",
  fields: [{ key: "text", label: "Tekst", type: "richText" }],
});

export const bikiniSectionDefinitions: Record<(typeof bikiniSectionTypes)[number], LandingSectionDefinition> = {
  bikiniHero: {
    label: "Hero Bikini (zdjęcia i etykiety)",
    group: GROUP,
    fields: [
      { key: "title", label: "Nagłówek", type: "richText", help: "<em>tekst</em> = różowe wyróżnienie." },
      { key: "imageLeft", label: "Zdjęcie po lewej (desktop)", type: "image" },
      { key: "imageRight", label: "Zdjęcie po prawej (duży ekran)", type: "image" },
      { key: "imageMobile", label: "Zdjęcie na telefonie", type: "image" },
      {
        key: "labels",
        label: "Etykiety",
        type: "list",
        itemLabel: "Etykieta",
        fields: [
          { key: "text", label: "Tekst", type: "text" },
          { key: "icon", label: "Ikona", type: "select", options: ICON_OPTIONS },
        ],
      },
      ...ctaFields(),
    ],
    defaultProps: bikiniHeroProps,
  },
  bikiniBoxes: {
    label: "Trzy karty z ikonami",
    group: GROUP,
    fields: [
      classNameField,
      {
        key: "items",
        label: "Karty",
        type: "list",
        itemLabel: "Karta",
        fields: [
          { key: "icon", label: "Ikona", type: "select", options: ICON_OPTIONS },
          { key: "title", label: "Tytuł", type: "text" },
          { key: "text", label: "Tekst", type: "textarea" },
          { key: "background", label: "Tło karty", type: "color" },
        ],
      },
      ...ctaFields(),
    ],
    defaultProps: bikiniBoxesProps,
  },
  bikiniVideo: {
    label: "Film Wistia z przyciskiem",
    group: GROUP,
    fields: [
      { key: "title", label: "Nagłówek", type: "richText", help: "<em>tekst</em> = różowe wyróżnienie." },
      { key: "videoId", label: "Identyfikator filmu Wistia", type: "video" },
      { key: "background", label: "Tło sekcji", type: "color" },
      classNameField,
      { key: "overlayTitle", label: "Napis na filmie", type: "text" },
      { key: "caption", label: "Podpis pod filmem", type: "text" },
      ...ctaFields(),
    ],
    defaultProps: bikiniVideoProps,
  },
  bikiniMetamorphoses: {
    label: "Metamorfozy (karuzele i opis wyzwania)",
    group: GROUP,
    fields: [
      { key: "title", label: "Nagłówek", type: "text" },
      classNameField,
      {
        key: "mode",
        label: "Karuzele",
        type: "select",
        options: [
          { label: "Zdjęcia przed i po", value: "images" },
          { label: "Karty metamorfoz Hollywood z filmami", value: "hollywood" },
        ],
      },
      {
        key: "images1",
        label: "Zdjęcia w karuzeli 1",
        type: "list",
        itemLabel: "Zdjęcie",
        fields: [{ key: "image", label: "Zdjęcie", type: "image" }],
      },
      {
        key: "images2",
        label: "Zdjęcia w karuzeli 2",
        type: "list",
        itemLabel: "Zdjęcie",
        fields: [{ key: "image", label: "Zdjęcie", type: "image" }],
      },
      ...cardsFields(1),
      ...cardsFields(2),
      paragraphsField("description1", "Opis między karuzelami"),
      paragraphsField("description2", "Opis pod karuzelami"),
    ],
    defaultProps: bikiniMetamorphosesProps,
  },
  bikiniStatNumbers: {
    label: "Liczby wyzwania (12 000 kobiet...)",
    group: GROUP,
    fields: [
      {
        key: "items",
        label: "Liczby",
        type: "list",
        itemLabel: "Liczba",
        fields: [
          { key: "value", label: "Wartość", type: "text" },
          { key: "label", label: "Opis", type: "text" },
        ],
      },
    ],
    defaultProps: bikiniStatNumbersProps,
  },
  bikiniWays: {
    label: "Dwie drogi (Możesz tylko wygrać)",
    group: GROUP,
    fields: [
      { key: "title", label: "Nagłówek", type: "text" },
      { key: "subtitle", label: "Podtytuł", type: "text" },
      {
        key: "ways",
        label: "Drogi",
        type: "list",
        itemLabel: "Droga",
        fields: [
          { key: "label", label: "Etykieta", type: "text" },
          { key: "title", label: "Tytuł", type: "text" },
          { key: "steps", label: "Kroki (jeden w linii)", type: "stringList" },
          { key: "result", label: "Wynik", type: "text" },
          { key: "background", label: "Tło karty", type: "color" },
          {
            key: "resultStyle",
            label: "Styl wyniku",
            type: "select",
            options: [
              { label: "Delikatny", value: "soft" },
              { label: "Wyróżniony", value: "bold" },
            ],
          },
        ],
      },
      ...ctaFields(),
      { key: "note", label: "Tekst pod przyciskiem", type: "text", help: "Można użyć {{cena}}." },
    ],
    defaultProps: bikiniWaysProps,
  },
  bikiniIncluded: {
    label: "Co znajdziesz w wyzwaniu (karty ze zdjęciami)",
    group: GROUP,
    fields: [
      { key: "title", label: "Nagłówek", type: "text" },
      { key: "background", label: "Tło sekcji", type: "color" },
      classNameField,
      ...(["boxes1", "boxes2"] as const).map((key, index): LandingSectionField => ({
        key,
        label: index === 0 ? "Karty w pierwszym rzędzie" : "Karty w drugim rzędzie",
        type: "list",
        itemLabel: "Karta",
        fields: [
          { key: "image", label: "Zdjęcie (desktop)", type: "image" },
          { key: "imageMobile", label: "Zdjęcie (telefon)", type: "image" },
          { key: "title", label: "Tytuł", type: "text" },
          { key: "text", label: "Tekst", type: "textarea" },
        ],
      })),
      ...ctaFields(),
    ],
    defaultProps: bikiniIncludedProps,
  },
  bikiniHowItWorks: {
    label: "Jak wygląda wyzwanie (karty z ikonami)",
    group: GROUP,
    fields: [
      { key: "title", label: "Nagłówek", type: "richText", help: "<em>tekst</em> = różowe wyróżnienie." },
      { key: "background", label: "Tło sekcji", type: "color" },
      ...(["boxes1", "boxes2"] as const).map((key, index): LandingSectionField => ({
        key,
        label: index === 0 ? "Karty w pierwszym rzędzie" : "Karty w drugim rzędzie",
        type: "list",
        itemLabel: "Karta",
        fields: [
          { key: "image1", label: "Ikona 1", type: "image" },
          { key: "image2", label: "Ikona 2", type: "image" },
          { key: "text", label: "Tekst", type: "textarea" },
        ],
      })),
      ...ctaFields(),
    ],
    defaultProps: bikiniHowItWorksProps,
  },
  bikiniPriceBanner: {
    label: "Baner z ceną (Zacznij już teraz)",
    group: GROUP,
    fields: [
      { key: "title", label: "Nagłówek", type: "text" },
      {
        key: "priceText",
        label: "Cena z rabatem",
        type: "richText",
        help: "Gdy funnel ma cenę przed rabatem. <s>tekst</s> = przekreślone.",
      },
      { key: "priceTextWithoutDiscount", label: "Cena bez rabatu", type: "richText" },
      {
        key: "priceTextWithoutFunnel",
        label: "Cena, gdy funnel nie ma cen",
        type: "richText",
      },
      { key: "background", label: "Tło baneru (CSS background)", type: "text" },
      ...ctaFields(),
    ],
    defaultProps: bikiniPriceBannerProps,
  },
  bikiniFeatures: {
    label: "Cechy programu (karty z emoji)",
    group: GROUP,
    fields: [
      { key: "title", label: "Nagłówek", type: "richText", help: "<em>tekst</em> = różowe wyróżnienie." },
      ...(["itemsTop", "itemsBottom"] as const).map((key, index): LandingSectionField => ({
        key,
        label: index === 0 ? "Karty w pierwszym rzędzie" : "Karty w drugim rzędzie",
        type: "list",
        itemLabel: "Karta",
        fields: [
          { key: "emoji", label: "Emoji", type: "text" },
          { key: "title", label: "Tytuł", type: "text" },
          { key: "text", label: "Tekst", type: "textarea" },
        ],
      })),
      ...ctaFields(),
    ],
    defaultProps: bikiniFeaturesProps,
  },
  bikiniNonForbiddenProducts: {
    label: "To NIE są zakazane produkty",
    group: GROUP,
    fields: [
      { key: "title", label: "Nagłówek", type: "text" },
      {
        key: "items",
        label: "Produkty",
        type: "list",
        itemLabel: "Produkt",
        fields: [
          { key: "image", label: "Zdjęcie", type: "image" },
          { key: "alt", label: "Opis zdjęcia", type: "text" },
          { key: "width", label: "Szerokość (desktop, px)", type: "number" },
          { key: "height", label: "Wysokość (desktop, px)", type: "number" },
          { key: "mobileWidth", label: "Szerokość (telefon, px)", type: "number" },
          { key: "mobileHeight", label: "Wysokość (telefon, px)", type: "number" },
        ],
      },
      ...ctaFields(),
    ],
    defaultProps: bikiniNonForbiddenProductsProps,
  },
  bikiniRecommendations: {
    label: "Karuzela uczestniczek z opisem programu",
    group: GROUP,
    fields: [
      { key: "title", label: "Nagłówek", type: "richText", help: "<em>tekst</em> = różowe wyróżnienie." },
      {
        key: "items",
        label: "Uczestniczki",
        type: "list",
        itemLabel: "Uczestniczka",
        fields: [
          { key: "image", label: "Zdjęcie", type: "image" },
          { key: "name", label: "Imię", type: "text" },
          { key: "caption", label: "Podpis", type: "text" },
        ],
      },
      { key: "description", label: "Opis", type: "richText" },
      ...ctaFields(),
    ],
    defaultProps: bikiniRecommendationsProps,
  },
  bikiniMoneyBack: {
    label: "Gwarancja 300% (skarbonka)",
    group: GROUP,
    fields: [
      { key: "title", label: "Nagłówek", type: "richText", help: "<em>tekst</em> = różowe wyróżnienie." },
      { key: "text", label: "Tekst", type: "richText" },
      { key: "imageMobile", label: "Zdjęcie (telefon)", type: "image" },
      { key: "imageDesktop", label: "Zdjęcie (desktop)", type: "image" },
      { key: "imageAlt", label: "Opis zdjęcia", type: "text" },
      ...ctaFields(),
    ],
    defaultProps: bikiniMoneyBackProps,
  },
  bikiniFaq: {
    label: "Najczęściej zadawane pytania",
    group: GROUP,
    fields: [
      { key: "title", label: "Nagłówek", type: "text" },
      {
        key: "items",
        label: "Pytania",
        type: "list",
        itemLabel: "Pytanie",
        fields: [
          { key: "question", label: "Pytanie", type: "text" },
          { key: "answer", label: "Odpowiedź", type: "richText" },
        ],
      },
      { key: "openFirst", label: "Pierwsze pytanie rozwinięte", type: "boolean" },
      ...ctaFields(),
    ],
    defaultProps: bikiniFaqProps,
  },
  bikiniFooter: {
    label: "Stopka Bikini",
    group: GROUP,
    fields: [
      { key: "termsLabel", label: "Tekst linku do regulaminu", type: "text" },
      { key: "termsUrl", label: "Link do regulaminu", type: "url" },
      { key: "privacyLabel", label: "Tekst linku do polityki prywatności", type: "text" },
      { key: "privacyUrl", label: "Link do polityki prywatności", type: "url" },
      { key: "facebookUrl", label: "Facebook", type: "url" },
      { key: "instagramUrl", label: "Instagram", type: "url" },
      { key: "youtubeUrl", label: "YouTube", type: "url" },
      {
        key: "scrollToTopOffset",
        label: "Położenie przycisku do góry",
        type: "select",
        options: [
          { label: "Przy dolnej krawędzi", value: "bottom-8" },
          { label: "Nad przyklejonym paskiem", value: "bottom-28" },
        ],
      },
    ],
    defaultProps: bikiniFooterProps,
  },
  bikiniStickyBar: {
    label: "Przyklejony pasek z ceną",
    group: GROUP,
    fields: [
      { key: "title", label: "Tytuł", type: "text" },
      { key: "oldPrice", label: "Cena przekreślona", type: "text", help: "Można użyć {{cenaPrzedRabatem}}." },
      { key: "price", label: "Cena", type: "text", help: "Można użyć {{cena}}." },
      { key: "ctaText", label: "Tekst przycisku", type: "text" },
      { key: "ctaTextCompact", label: "Tekst przycisku na telefonie", type: "text", help: "Puste = cena i strzałka." },
      { key: "href", label: "Link przycisku", type: "url", help: "Puste = następny krok funnelu (checkout)." },
    ],
    defaultProps: bikiniStickyBarProps,
  },
};
